import type { FC } from "react";
import { useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { abbrNumberFilter } from "app/filters/ngFilters";
import TagsBadge from "app/pages/sales-intelligence/common-components/TagsBadge/TagsBadge";
import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import type { TagWithDomain } from "app/pages/sales-intelligence/modules/company-analysis/types/company";
import { urlFilterValues } from "app/pages/sales-intelligence/modules/leads-search-filters";
import { MAX_TAGS_AMOUNT } from "app/pages/sales-intelligence/pages/company-analysis/constants/index";
import { StyledButton } from "app/pages/sales-intelligence/pages/company-analysis/containers/SiteTagsContainer/styles";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { is } from "app/pages/sales-intelligence/utils/type-guards";
import { capitalize } from "lodash";

const EMPTY_TAGS: readonly (TagWithDomain | string)[] = [];

const isSiteTag = (item: unknown): item is TagWithDomain | string =>
    is.string(item) || (item != null && typeof item === "object" && "tag" in item && is.string(item.tag));

type SiteTagsContainerProps = {
    siteTags?: readonly (TagWithDomain | string)[];
};

export const SiteTagsContainer: FC<SiteTagsContainerProps> = ({ siteTags = EMPTY_TAGS }) => {
    const { translate } = useTranslate();

    const [isShowFullList, setIsShowFullList] = useState<boolean>(false);

    const isShowMore = siteTags?.length > MAX_TAGS_AMOUNT;

    const topSiteTags: (TagWithDomain | string)[] = useMemo(() => {
        return Array.isArray(siteTags)
            ? siteTags.filter(isSiteTag).slice(0, isShowFullList ? siteTags.length : MAX_TAGS_AMOUNT)
            : [];
    }, [isShowFullList, siteTags]);

    if (!siteTags?.length) {
        return null;
    }

    const handleGoToAdvancedSearch = (tag?: string) => {
        companyAnalysisTrackingService.overview.trackTagClick(tag);

        if (!tag) {
            return;
        }

        void siNavigator.goToAdvancedSearchPage(undefined, { filtersPayload: urlFilterValues.siteTag(tag) });
    };

    return (
        <Box data-automation="site-tags-container">
            <Text
                fontVariant="caption"
                color={colorsPalettes.carbon[250]}
                margin="4px 0"
                display="block"
            >
                {`${translate("si.company-analysis.site-tags.caption")}:`}
            </Text>
            <Flex
                gap="8px"
                flexWrap="wrap"
            >
                {topSiteTags.map((siteTag) => {
                    const tag = typeof siteTag === "string" ? siteTag : siteTag.tag;
                    return (
                        <Tooltip
                            content={translate("si.company-analysis.site-tag.similar-companies", {
                                count: abbrNumberFilter()(typeof siteTag === "string" ? 0 : siteTag.domainsCount),
                            })}
                        >
                            <div>
                                <TagsBadge
                                    key={tag}
                                    text={capitalize(tag)}
                                    dataAutomationType="site-tag"
                                    onClick={() => handleGoToAdvancedSearch(tag)}
                                />
                            </div>
                        </Tooltip>
                    );
                })}

                {isShowMore ? (
                    isShowFullList ? (
                        <StyledButton
                            type="flat"
                            onClick={() => setIsShowFullList(!isShowFullList)}
                        >
                            {translate("si.common.show_less")}
                        </StyledButton>
                    ) : (
                        <TagsBadge
                            key="show-more-site-tag-hide"
                            text={`+ ${siteTags.length - MAX_TAGS_AMOUNT}`}
                            dataAutomationType="show-more-site-tag"
                            onClick={() => setIsShowFullList(!isShowFullList)}
                        />
                    )
                ) : null}
            </Flex>
        </Box>
    );
};
